import {
  Button,
  DialogActions,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Box,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import PredictiveOccupancyDetectionFormFields from './PredictiveOccupancyDetectionFormFields';
import BuildingProtectionOccupancyFormFields from './BuildingProtectionOccupancyFormFields';
import PredictiveFrostProtectionFormFields from './PredictiveFrostProtectionFormFields';
import InitializationFormFields from './InitializationFormFields';

const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true; // Handle primitive values

  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  ) {
    return false; // Handle cases where either is not an object or is null
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false; // Different number of keys

  for (let key of keys1) {
    if (!keys2.includes(key)) return false; // Key missing in obj2
    if (!deepEqual(obj1[key], obj2[key])) return false; // Recursive comparison
  }

  return true;
};

const AlgorithmInputSimpleDialog = ({
  open,
  onClose,
  update,
  specificationData,
  subscription,
  updateMachineLearningModel,
  disableAlgorithmInputForm,
}) => {
  if (!subscription || !specificationData) {
    return null; // or a loading spinner, or some other placeholder
  }

  const [formState, setFormState] = useState(subscription.AlgorithmInput || {});
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [pendingFormState, setPendingFormState] = useState(null); // Holds finalFormState during the dialog

  const [countdown, setCountdown] = useState(5); // Start countdown at 5 seconds
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [includePredictiveOccupancy, setIncludePredictiveOccupancy] = useState(
    Boolean(subscription.AlgorithmInput?.predictive_occupancy_detection),
  );

  const [includeBuildingProtection, setIncludeBuildingProtection] = useState(
    Boolean(subscription.AlgorithmInput?.building_protection),
  );

  const [
    includePredictiveFrostProtection,
    setIncludePredictiveFrostProtection,
  ] = useState(
    Boolean(subscription.AlgorithmInput?.predictive_frost_protection),
  );

  const [includeInitialization, setIncludeInitialization] = useState(
    Boolean(subscription.AlgorithmInput?.initialization),
  );

  const handleSectionSubmit = (section, data) => {
    setFormState((prevState) => ({
      ...prevState,
      [section]: {
        ...prevState[section],
        ...data,
      },
    }));
  };

  const handleResetOccupancyDetectionModel = async () => {
    const body = {
      reset_predictive_occupancy_detection: true,
      reset_preheat: false,
      reset_cooldown: false,
    };

    if (pendingFormState) {
      try {
        await submitForm(pendingFormState); // Submit the saved form state
      } catch (error) {
        console.error('Failed to submit form:', error);
        return; // Exit the function if submitForm encounters an error
      }
    }

    // Add a delay before calling updateMachineLearningModel
    setTimeout(() => {
      try {
        updateMachineLearningModel({ id: subscription.id, data: body });
      } catch (error) {
        console.error('Failed to update machine learning model:', error);
      }
    }, 2000); // 2-second delay
  };

  useEffect(() => {
    let timer; // Variable to store the timer reference

    if (promptDialogOpen) {
      setCountdown(5); // Reset the countdown to 5 whenever the dialog opens
      timer = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown > 1) {
            return prevCountdown - 1; // Decrement the countdown
          } else {
            clearInterval(timer); // Clear the timer when countdown reaches 0
            setIsButtonDisabled(false); // Enable the button
            return 0; // Ensure countdown stays at 0
          }
        });
      }, 1000);
    } else {
      clearInterval(timer); // Clear the timer if the dialog closes
    }

    return () => clearInterval(timer); // Cleanup the timer on component unmount or state change
  }, [promptDialogOpen]);

  const handleSubmit = () => {
    const finalFormState = { ...formState };

    // Conditionally delete predictive_occupancy_detection if not included
    if (!includePredictiveOccupancy)
      delete finalFormState.predictive_occupancy_detection;
    if (!includeBuildingProtection) delete finalFormState.building_protection;
    if (!includePredictiveFrostProtection)
      delete finalFormState.predictive_frost_protection;
    if (!includeInitialization) delete finalFormState.initialization;

    // Extract baseline_model for comparison
    const initialBaselineModel =
      subscription.AlgorithmInput?.predictive_occupancy_detection
        ?.baseline_model || {};
    const finalBaselineModel =
      finalFormState.predictive_occupancy_detection?.baseline_model || {};

    // Check for changes in baseline_model
    const baselineModelChanged = !deepEqual(
      initialBaselineModel,
      finalBaselineModel,
    );

    // If changes detected, prompt the user
    if (baselineModelChanged) {
      setPromptDialogOpen(true); // Open a Material-UI dialog
      setPendingFormState(finalFormState); // Save the prepared form state
      return; // Stop further execution until the user confirms
    }

    submitForm(finalFormState); // Proceed with form submission
  };

  // Function to handle the actual form submission
  const submitForm = (updatedFormState) => {
    const putData = {
      DataInput: subscription.DataInput,
      UserInput: subscription.UserInput,
      AlgorithmInput: updatedFormState,
      DisplayName: subscription.DisplayName,
    };

    try {
      // Perform the update operation (uncomment the update call)
      const response = update(putData);
      onClose();
      return response;
    } catch (error) {
      console.error('Failed to update subscription:', error);
    }
  };

  const resetForm = () => {
    setFormState({});
    setCountdown(5);
    setIsButtonDisabled(true);
    onClose();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle>Algorithm Input</DialogTitle>
        <DialogContent dividers>
          <FormControlLabel
            control={
              <Switch
                checked={includePredictiveOccupancy}
                onChange={() => setIncludePredictiveOccupancy((prev) => !prev)}
              />
            }
            label="Predictive Occupancy Detection"
          />
          {includePredictiveOccupancy && (
            <PredictiveOccupancyDetectionFormFields
              data={formState.predictive_occupancy_detection || {}}
              predictiveOccupancyDetectionSpecification={
                specificationData.predictive_occupancy_detection
              }
              onSectionSubmit={(data) =>
                handleSectionSubmit('predictive_occupancy_detection', data)
              }
            />
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={includeBuildingProtection}
                onChange={() => setIncludeBuildingProtection((prev) => !prev)}
              />
            }
            label="Include Building Protection"
          />
          {includeBuildingProtection && (
            <BuildingProtectionOccupancyFormFields
              data={specificationData.building_protection}
              sectionState={formState.building_protection || {}}
              onSectionSubmit={(data) =>
                handleSectionSubmit('building_protection', data)
              }
            />
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={includePredictiveFrostProtection}
                onChange={() =>
                  setIncludePredictiveFrostProtection((prev) => !prev)
                }
              />
            }
            label="Include Predictive Frost Protection"
          />
          {includePredictiveFrostProtection && (
            <PredictiveFrostProtectionFormFields
              data={specificationData.predictive_frost_protection}
              sectionState={formState.predictive_frost_protection || {}}
              onSectionSubmit={(data) =>
                handleSectionSubmit('predictive_frost_protection', data)
              }
            />
          )}

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={includeInitialization}
                onChange={() => setIncludeInitialization((prev) => !prev)}
              />
            }
            label="Include Initialization"
          />

          {includeInitialization && (
            <InitializationFormFields
              data={specificationData.initialization}
              sectionState={formState.initialization || {}}
              onSectionSubmit={(data) =>
                handleSectionSubmit('initialization', data)
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            variant="text"
            disabled={disableAlgorithmInputForm}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={disableAlgorithmInputForm}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={promptDialogOpen}
        onClose={() => setPromptDialogOpen(false)}
      >
        <DialogTitle>Important Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have made an input to the algorithm, that requries a reset of
            the model for it to be executed. Remember to reset the model. The
            implications of resetting the model are large, please be sure that
            you want to reset.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              color="warning"
              onClick={handleResetOccupancyDetectionModel}
              disabled={isButtonDisabled}
              sx={{ textTransform: 'none' }}
            >
              {isButtonDisabled
                ? `Wait ${countdown}s`
                : 'Reset Occupancy Detection Model'}
            </Button>
            <Box>
              <Button
                sx={{ textTransform: 'none' }}
                onClick={() => setPromptDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setPromptDialogOpen(false);
                  if (pendingFormState) {
                    submitForm(pendingFormState); // Submit the saved form state
                  }
                }}
                sx={{ ml: 1, textTransform: 'none' }}
                variant="contained"
              >
                Confirm without reset
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AlgorithmInputSimpleDialog;
